/**
 * Environment Configuration
 * Validates and exports environment variables with type safety
 * Fails fast if required variables are missing in production
 */

const isDevelopment = import.meta.env.DEV;
const isProduction = import.meta.env.PROD;

/**
 * Required environment variables
 * These must be set for the app to function
 */
const REQUIRED_ENV_VARS = ['VITE_API_BASE_URL', 'VITE_THIRDWEB_CLIENT_ID'];

/**
 * Optional environment variables with defaults
 * Note: VITE_TINYMCE_KEY removed - TinyMCE is now self-hosted (no API key needed)
 */
const OPTIONAL_ENV_VARS = {};

/**
 * Validate that all required environment variables are set
 * In production, throws an error if any are missing
 * In development, logs a warning
 */
function validateEnv() {
  const missing = [];

  for (const varName of REQUIRED_ENV_VARS) {
    if (!import.meta.env[varName]) {
      missing.push(varName);
    }
  }

  if (missing.length > 0) {
    const message = `Missing required environment variables: ${missing.join(
      ', '
    )}`;

    if (isProduction) {
      throw new Error(message);
    } else {
      console.warn(`[ENV Warning] ${message}`);
    }
  }
}

// Validate on module load
validateEnv();

/**
 * Get an environment variable with optional default
 * @param {string} key - Environment variable name
 * @param {string} [defaultValue] - Default value if not set
 * @returns {string} The environment variable value
 */
export function getEnv(key, defaultValue = '') {
  return import.meta.env[key] || defaultValue;
}

/**
 * Exported environment configuration
 */
export const env = {
  // App
  isDevelopment,
  isProduction,

  // API
  apiBaseUrl: getEnv('VITE_API_BASE_URL'),

  // Third-party services
  thirdwebClientId: getEnv('VITE_THIRDWEB_CLIENT_ID'),
  // Note: TinyMCE is now self-hosted - no API key needed

  // Feature flags (can be extended)
  enableDevTools: isDevelopment,
};

export default env;
